import { Search, X } from 'lucide-react';
import type { JSX, ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button.js';
import { Input } from '@/components/ui/input.js';

export interface ListToolbarSearch {
  value: string;
  onChange: (next: string) => void;
  placeholder?: string;
  label?: string;
  /**
   * Opt-in : remonte aussi la saisie après ce délai sans frappe (ms), en plus
   * d'Entrée et de la perte de focus. Absent = validation explicite seulement.
   */
  debounceMs?: number;
}

export interface ListToolbarProps {
  search: ListToolbarSearch;
  /** Filtres de l'écran, alignés à droite de la recherche (contrôles de 36 px). */
  children?: ReactNode;
  /** Affiche « Réinitialiser » quand au moins un filtre est posé. */
  isFiltered?: boolean;
  onReset?: () => void;
}

/**
 * Gabarit commun des barres de liste : recherche puis filtres sur une même
 * ligne, qui passe à la ligne sans déborder sur petit écran. La recherche tient
 * un brouillon local et ne remonte qu'à la validation (Entrée) ou à la perte
 * de focus, pour ne pas relancer une requête à chaque frappe ; avec
 * `search.debounceMs`, elle remonte aussi après une pause de frappe.
 */
export function ListToolbar({
  search,
  children,
  isFiltered = false,
  onReset,
}: ListToolbarProps): JSX.Element {
  const [draft, setDraft] = useState(search.value);

  useEffect(() => {
    // Ne pas écraser une saisie équivalente (espace final en cours de frappe).
    setDraft((current) => (current.trim() === search.value ? current : search.value));
  }, [search.value]);

  function commit(next: string): void {
    const trimmed = next.trim();
    if (trimmed !== search.value) {
      search.onChange(trimmed);
    }
  }

  const { debounceMs } = search;
  useEffect(() => {
    if (debounceMs === undefined) {
      return undefined;
    }
    const handle = window.setTimeout(() => {
      commit(draft);
    }, debounceMs);
    return () => {
      window.clearTimeout(handle);
    };
    // `commit` lit `search` à jour à chaque rendu ; seul le brouillon relance le délai.
  }, [draft, debounceMs]);

  const label = search.label ?? 'Rechercher';

  return (
    <div className="flex w-full flex-wrap items-center gap-2">
      <div className="relative w-full sm:w-72">
        <Search
          className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
          aria-hidden="true"
        />
        <Input
          type="search"
          value={draft}
          placeholder={search.placeholder ?? 'Rechercher…'}
          aria-label={label}
          className="w-full pl-8 pr-9 [&::-webkit-search-cancel-button]:hidden"
          onChange={(event) => {
            setDraft(event.target.value);
          }}
          onBlur={() => {
            commit(draft);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              commit(draft);
            }
          }}
        />
        {draft !== '' ? (
          <button
            type="button"
            aria-label="Effacer la recherche"
            className="absolute right-1.5 top-1/2 inline-flex size-6 -translate-y-1/2 items-center justify-center rounded-sm text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            onClick={() => {
              setDraft('');
              commit('');
            }}
          >
            <X className="size-3.5" aria-hidden="true" />
          </button>
        ) : null}
      </div>

      {children}
      {isFiltered && onReset ? (
        <Button type="button" variant="ghost" onClick={onReset}>
          <X aria-hidden="true" />
          Réinitialiser
        </Button>
      ) : null}
    </div>
  );
}
